/**
 * Qt type wrappers. Each class dictates how a JavaScript value is serialized
 * to a byte buffer and how it is read back.
 *
 * @module qtdatastream-web/types
 */
import { concat, decodeUtf16BE, encodeUtf16BE, encodeUtf8, fromBigInt64BE, fromBigUint64BE, fromDoubleBE, fromInt8, fromInt32BE, fromUint16BE, fromUint32BE, toUint8, } from './bytes.js';
import { dateToJulianDay, julianDayToDate, str as bstr } from './util.js';
/**
 * Qt type constants.
 * @see {@link http://doc.qt.io/qt-4.8/qvariant.html#Type-enum}
 * @see {@link http://doc.qt.io/qt-4.8/datastreamformat.html}
 */
export const Types = {
    INVALID: 0,
    BOOL: 1,
    INT: 2,
    UINT: 3,
    INT64: 4,
    UINT64: 5,
    DOUBLE: 6,
    CHAR: 7,
    MAP: 8,
    LIST: 9,
    STRING: 10,
    STRINGLIST: 11,
    BYTEARRAY: 12,
    TIME: 15,
    DATETIME: 16,
    USERTYPE: 127,
    SHORT: 133,
};
/** If `obj` is serializable (has an `export()` method), call it; else return it. */
export function prepare(obj) {
    return obj !== undefined && obj !== null && typeof obj.export === 'function'
        ? obj.export()
        : obj;
}
/**
 * Abstract base that every Qt type wrapper extends. Subclasses decide how a
 * value is represented as a byte buffer, and vice-versa.
 */
export class QClass {
    __obj;
    static qtype;
    static types = new Map();
    constructor(obj) {
        this.__obj = obj;
    }
    static from(subject, force = false) {
        const ctor = this;
        if (subject instanceof ctor && !force) {
            return subject;
        }
        subject = prepare(subject);
        if (subject instanceof ctor) {
            return subject;
        }
        return new ctor(subject);
    }
    /**
     * Read a value of this type from a buffer. Concrete subclasses override this;
     * the base implementation documents the contract and throws if called.
     */
    static read(_buffer, _name) {
        throw new Error(`${this.name}.read() is not implemented`);
    }
}
/**
 * Register a QClass against a QVariant type id so it can be resolved during
 * (de)serialization.
 */
function qtype(qvarianttype) {
    return function (target) {
        if (qvarianttype !== undefined) {
            QClass.types.set(qvarianttype, target);
        }
        target.qtype = qvarianttype;
    };
}
export class QInvalid extends QClass {
    static read(_buffer) {
        return undefined;
    }
    toBuffer() {
        return new Uint8Array(0);
    }
}
qtype(Types.INVALID)(QInvalid);
export class QBool extends QClass {
    static read(buffer) {
        return Boolean(buffer.readInt8());
    }
    toBuffer() {
        return fromInt8(this.__obj ? 1 : 0);
    }
}
qtype(Types.BOOL)(QBool);
export class QShort extends QClass {
    static read(buffer) {
        return buffer.readInt16BE();
    }
    toBuffer() {
        return fromUint16BE(this.__obj);
    }
}
qtype(Types.SHORT)(QShort);
export class QInt extends QClass {
    static read(buffer) {
        return buffer.readInt32BE();
    }
    toBuffer() {
        return fromInt32BE(this.__obj);
    }
}
qtype(Types.INT)(QInt);
export class QUInt extends QClass {
    static read(buffer) {
        return buffer.readUInt32BE();
    }
    toBuffer() {
        return fromUint32BE(this.__obj);
    }
}
qtype(Types.UINT)(QUInt);
export class QInt64 extends QClass {
    static read(buffer) {
        return buffer.readInt64BE();
    }
    toBuffer() {
        return fromBigInt64BE(this.__obj);
    }
}
qtype(Types.INT64)(QInt64);
export class QUInt64 extends QClass {
    static read(buffer) {
        return buffer.readUInt64BE();
    }
    toBuffer() {
        return fromBigUint64BE(this.__obj);
    }
}
qtype(Types.UINT64)(QUInt64);
export class QDouble extends QClass {
    static read(buffer) {
        return buffer.readDoubleBE();
    }
    toBuffer() {
        return fromDoubleBE(this.__obj);
    }
}
qtype(Types.DOUBLE)(QDouble);
export class QChar extends QClass {
    constructor(obj) {
        super(obj);
        if (typeof this.__obj !== 'string')
            throw new Error(`${this.__obj} is not a string`);
        if (this.__obj.length !== 1)
            throw new Error(`${this.__obj} length must equal 1`);
    }
    static read(buffer) {
        return decodeUtf16BE(buffer.slice(2));
    }
    toBuffer() {
        return encodeUtf16BE(this.__obj);
    }
}
qtype(Types.CHAR)(QChar);
export class QTime extends QUInt {
}
qtype(Types.TIME)(QTime);
export class QByteArray extends QClass {
    /** Reads the raw bytes as a Uint8Array view (use `util.str` to get a string). */
    static read(buffer) {
        const arraySize = QUInt.read(buffer);
        if (arraySize === 0 || arraySize === 0xffffffff)
            return null;
        return buffer.slice(arraySize);
    }
    toBuffer() {
        if (this.__obj === null) {
            return fromUint32BE(0xffffffff);
        }
        const buf = typeof this.__obj === 'string' ? encodeUtf8(this.__obj) : toUint8(this.__obj);
        return concat([fromUint32BE(buf.length), buf]);
    }
}
qtype(Types.BYTEARRAY)(QByteArray);
export class QString extends QClass {
    static read(buffer) {
        const stringSize = QUInt.read(buffer);
        if (stringSize === 0 || stringSize === 0xffffffff)
            return '';
        return decodeUtf16BE(buffer.slice(stringSize));
    }
    toBuffer() {
        if (this.__obj === null) {
            return fromUint32BE(0xffffffff);
        }
        const value = typeof this.__obj === 'number' ? String(this.__obj) : this.__obj;
        const bufstring = encodeUtf16BE(value);
        return concat([fromUint32BE(bufstring.length), bufstring]);
    }
}
qtype(Types.STRING)(QString);
export class QList extends QClass {
    static read(buffer) {
        const listSize = QUInt.read(buffer);
        const l = new Array(listSize);
        for (let i = 0; i < listSize; i++) {
            l[i] = QVariant.read(buffer);
        }
        return l;
    }
    /** Create a QList subclass whose elements are coerced to a given QClass/usertype. */
    static of(qclass) {
        const elementClass = typeof qclass === 'string' ? QUserType.get(qclass) : qclass;
        const parent = this;
        return class extends parent {
            static from(subject) {
                if (Array.isArray(subject)) {
                    subject = subject.map((elt) => prepare(elementClass.from(elt)));
                }
                return parent.from(subject);
            }
        };
    }
    toBuffer() {
        const bufs = [fromUint32BE(this.__obj.length)];
        for (const el of this.__obj) {
            bufs.push(QVariant.from(el).toBuffer());
        }
        return concat(bufs);
    }
}
qtype(Types.LIST)(QList);
export class QStringList extends QClass {
    static read(buffer) {
        const listSize = QUInt.read(buffer);
        const l = new Array(listSize);
        for (let i = 0; i < listSize; i++) {
            l[i] = QString.read(buffer);
        }
        return l;
    }
    toBuffer() {
        const bufs = [fromUint32BE(this.__obj.length)];
        for (const el of this.__obj) {
            bufs.push(QString.from(el).toBuffer());
        }
        return concat(bufs);
    }
}
qtype(Types.STRINGLIST)(QStringList);
export class QDateTime extends QClass {
    static read(buffer) {
        const julianDay = QUInt.read(buffer);
        const msecondsSinceMidnight = QUInt.read(buffer);
        QBool.read(buffer); // isUTC flag (unused on read)
        const dateAtMidnight = julianDayToDate(julianDay);
        dateAtMidnight.setMilliseconds(msecondsSinceMidnight);
        return dateAtMidnight;
    }
    toBuffer() {
        const date = this.__obj;
        const milliseconds = (date.getTime() - date.getTimezoneOffset() * 60000) % 86400000;
        const julianday = dateToJulianDay(date);
        return concat([
            fromUint32BE(julianday),
            fromUint32BE(milliseconds),
            QBool.from(date.getTimezoneOffset() === 0).toBuffer(),
        ]);
    }
}
qtype(Types.DATETIME)(QDateTime);
export class QMap extends QClass {
    static read(buffer) {
        const mapSize = QUInt.read(buffer);
        const map = {};
        for (let i = 0; i < mapSize; i++) {
            const key = QString.read(buffer);
            map[key] = QVariant.read(buffer);
        }
        return map;
    }
    toBuffer() {
        const bufs = [];
        if (this.__obj instanceof Map) {
            bufs.push(fromUint32BE(this.__obj.size));
            for (const [key, value] of this.__obj) {
                bufs.push(QString.from(key).toBuffer());
                bufs.push(QVariant.from(value).toBuffer());
            }
        }
        else {
            const keys = this.__obj ? Object.keys(this.__obj) : [];
            bufs.push(fromUint32BE(keys.length));
            for (const key of keys) {
                bufs.push(QString.from(key).toBuffer());
                bufs.push(QVariant.from(this.__obj[key]).toBuffer());
            }
        }
        return concat(bufs);
    }
}
qtype(Types.MAP)(QMap);
export class QUserType extends QClass {
    name;
    static usertypes = new Map();
    constructor(name, obj) {
        super(obj);
        this.name = name;
    }
    static from(subject) {
        if (subject instanceof QUserType) {
            return subject;
        }
        return new this(subject);
    }
    /** Build a usertype class from a structured (array) definition. */
    static createComplexUserType(name, value) {
        const compiled = [];
        for (const type of value) {
            const [key] = Object.keys(type);
            const entry = {
                key,
                quserclass: undefined,
            };
            if (typeof type[key] === 'string') {
                entry.quserclassname = type[key];
                entry.quserclass = QUserType.usertypes.get(type[key]);
            }
            else {
                entry.quserclass = QClass.types.get(type[key]);
            }
            if (!entry.quserclass) {
                throw new Error(`Type ${type[key]} does not exists`);
            }
            compiled.push(entry);
        }
        return class extends QUserType {
            constructor(obj) {
                super(name, obj);
            }
            static read(buffer) {
                const obj = {};
                for (const elt of compiled) {
                    obj[elt.key] = elt.quserclass.read(buffer, elt.quserclassname);
                }
                return obj;
            }
            toBuffer(skipname = false) {
                const bufs = [this._getNameBuffer(skipname)];
                for (const elt of compiled) {
                    bufs.push(elt.quserclass.from(this.__obj[elt.key]).toBuffer(true));
                }
                return concat(bufs);
            }
        };
    }
    /** Build a usertype class from a simple (single Qt type) definition. */
    static createUserType(name, value) {
        if (Array.isArray(value)) {
            return QUserType.createComplexUserType(name, value);
        }
        const qclass = QClass.types.get(value);
        return class extends QUserType {
            constructor(obj) {
                super(name, obj);
            }
            static read(buffer) {
                return qclass.read(buffer);
            }
            toBuffer(skipname = false) {
                return concat([this._getNameBuffer(skipname), qclass.from(this.__obj).toBuffer(true)]);
            }
        };
    }
    /** Register a custom usertype under a name. */
    static register(name, value) {
        QUserType.usertypes.set(name, QUserType.createUserType(name, value));
    }
    /** Get a previously registered usertype class. */
    static get(name) {
        return QUserType.usertypes.get(name);
    }
    static read(buffer, name) {
        if (!name) {
            const bname = QByteArray.read(buffer);
            name = bstr(bname ?? new Uint8Array(0));
        }
        const usertype = QUserType.usertypes.get(name);
        if (!usertype) {
            throw new Error(`Unregistered usertype ${name}`);
        }
        return usertype.read(buffer);
    }
    _getNameBuffer(skipname) {
        if (!this.name) {
            throw new Error('Abstract QUserType cannot be converted to a buffer');
        }
        if (!skipname) {
            return QByteArray.from(this.name).toBuffer();
        }
        return new Uint8Array(0);
    }
    toBuffer(skipname = false) {
        return concat([
            this._getNameBuffer(skipname),
            QUserType.usertypes.get(this.name).from(this.__obj).toBuffer(true),
        ]);
    }
}
qtype(Types.USERTYPE)(QUserType);
export class QVariant extends QClass {
    static coerceNumbersClass = QUInt;
    /**
     * Change the default QClass that plain numbers are coerced to (default QUInt).
     */
    static coerceNumbersTo(type) {
        const qclass = QClass.types.get(type);
        if (qclass === undefined) {
            throw new Error(`undefined type ${type}`);
        }
        QVariant.coerceNumbersClass = qclass;
    }
    static read(buffer) {
        const type = QUInt.read(buffer);
        QBool.read(buffer); // isNull flag (unused on read)
        return QClass.types.get(type).read(buffer);
    }
    toBuffer() {
        const isNull = this.__obj === undefined || this.__obj === null;
        const typeofobj = typeof this.__obj;
        let qclass;
        if (this.__obj === undefined) {
            qclass = QInvalid;
        }
        else if (this.__obj instanceof QUserType) {
            qclass = QUserType;
        }
        else if (this.__obj instanceof QVariant) {
            throw new Error(`Can't nest QVariant`);
        }
        else if (this.__obj instanceof QClass) {
            qclass = this.__obj.constructor;
        }
        else if (typeofobj === 'string') {
            qclass = QString;
        }
        else if (typeofobj === 'bigint') {
            qclass = QInt64;
        }
        else if (typeofobj === 'number') {
            qclass = QVariant.coerceNumbersClass;
        }
        else if (typeofobj === 'boolean') {
            qclass = QBool;
        }
        else if (this.__obj instanceof Date) {
            qclass = QDateTime;
        }
        else if (Array.isArray(this.__obj)) {
            qclass = QList;
        }
        else {
            qclass = QMap;
        }
        const bufs = [fromUint32BE(qclass.qtype), QBool.from(isNull).toBuffer()];
        if (!isNull) {
            bufs.push(qclass.from(this.__obj).toBuffer());
        }
        return concat(bufs);
    }
}
export { qtype };
//# sourceMappingURL=types.js.map